import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import './OnboardingSlides.css';

const SLIDE1_IMG = "https://customer-assets.emergentagent.com/job_5781352b-a942-4599-857e-2365443d4505/artifacts/4okkh4yk_image_slide1.avif";
const SLIDE2_IMG = "https://customer-assets.emergentagent.com/job_5781352b-a942-4599-857e-2365443d4505/artifacts/h8yrhm6n_Image_slide2.jpg";
const VIDEO_SRC = "/bobine-onboarding.mp4";
const AUDIO_SRC = "/bobine-audio-seamless.mp3";

const SLIDE1_DURATION = 5000;
const SLIDE2_DURATION = 5000;
const PHASES = ['slide1', 'slide2', 'video', 'cta'];
const PHASE_VOLUMES = { slide1: 0.15, slide2: 0.45, video: 1.0, cta: 0 };

const OnboardingSlides = ({ onComplete }) => {
  const { t } = useLanguage();
  const [phase, setPhase] = useState('slide1');
  const [showText, setShowText] = useState(false);
  const [videoFading, setVideoFading] = useState(false);
  const [videoText, setVideoText] = useState(false);
  const [muted, setMuted] = useState(true);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const mutedRef = useRef(true);
  const fadeRef = useRef(null);

  const phaseIndex = PHASES.indexOf(phase);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // Crescendo simple via audio.volume
  const fadeVolumeTo = useCallback((targetVol, durationMs) => {
    if (fadeRef.current) clearInterval(fadeRef.current);
    const audio = audioRef.current;
    if (!audio) return;
    const startVol = mutedRef.current ? 0 : audio.volume;
    const steps = Math.max(1, Math.round(durationMs / 50));
    const delta = (targetVol - startVol) / steps;
    let step = 0;
    fadeRef.current = setInterval(() => {
      step++;
      const vol = Math.max(0, Math.min(1, startVol + delta * step));
      if (audio && !mutedRef.current) audio.volume = vol;
      if (step >= steps) { clearInterval(fadeRef.current); fadeRef.current = null; }
    }, 50);
  }, []);

  const unlockAudio = useCallback(() => {
    if (audioUnlocked) return;
    setAudioUnlocked(true);
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.15;
    audio.loop = false;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }, [audioUnlocked]);

  useEffect(() => {
    if (audioUnlocked) return;
    const handler = () => unlockAudio();
    document.addEventListener('click', handler, { capture: true, once: true });
    document.addEventListener('touchstart', handler, { capture: true, once: true });
    return () => {
      document.removeEventListener('click', handler, true);
      document.removeEventListener('touchstart', handler, true);
    };
  }, [audioUnlocked, unlockAudio]);

  useEffect(() => {
    return () => {
      if (fadeRef.current) clearInterval(fadeRef.current);
      try { audioRef.current?.pause(); } catch {}
      try { videoRef.current?.pause(); } catch {}
    };
  }, []);

  // Auto-advance + crescendo
  useEffect(() => {
    if (phase === 'slide1') {
      setShowText(false); setVideoText(false); setVideoFading(false);
      const t1 = setTimeout(() => setShowText(true), 800);
      const t2 = setTimeout(() => { setShowText(false); setTimeout(() => setPhase('slide2'), 600); }, SLIDE1_DURATION);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    if (phase === 'slide2') {
      setShowText(false);
      fadeVolumeTo(0.45, 2000);
      const t1 = setTimeout(() => setShowText(true), 600);
      const t2 = setTimeout(() => { setShowText(false); setTimeout(() => setPhase('video'), 600); }, SLIDE2_DURATION);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    if (phase === 'video') {
      setShowText(false);
      fadeVolumeTo(1.0, 1500);
      const video = videoRef.current;
      // Mobile: video MUST start muted for autoplay to work
      if (video) { video.muted = true; video.currentTime = 0; video.play().catch(() => {}); }
      const tt = setTimeout(() => setVideoText(true), 2000);
      return () => clearTimeout(tt);
    }
    if (phase === 'cta') {
      fadeVolumeTo(0, 1500);
      const t = setTimeout(() => { try { audioRef.current?.pause(); } catch {} }, 1600);
      return () => clearTimeout(t);
    }
  }, [phase, fadeVolumeTo]);

  const handleVideoEnded = () => { setVideoFading(true); setTimeout(() => setPhase('cta'), 1200); };

  const goNext = () => {
    if (phase === 'slide1') { setShowText(false); setTimeout(() => setPhase('slide2'), 500); }
    else if (phase === 'slide2') { setShowText(false); setTimeout(() => setPhase('video'), 500); }
    else if (phase === 'video') { setVideoFading(true); setTimeout(() => setPhase('cta'), 800); }
  };

  useEffect(() => {
    if (phase !== 'video') return;
    const t = setTimeout(() => { setVideoFading(true); setTimeout(() => setPhase('cta'), 1200); }, 12000);
    return () => clearTimeout(t);
  }, [phase]);

  const toggleMute = (e) => {
    e.stopPropagation();
    const next = !muted; setMuted(next);
    const audio = audioRef.current;
    const video = videoRef.current;
    // Audio background
    if (audio) {
      if (next) {
        audio.volume = 0;
      } else {
        // Force play in user gesture context
        audio.volume = PHASE_VOLUMES[phase] || 0.15;
        if (audio.paused) {
          audio.currentTime = 0;
          audio.play().catch(() => {});
        }
      }
    }
    // Video: on mobile, must pause→unmute→play in same gesture for sound to work
    if (video) {
      if (next) {
        video.muted = true;
      } else {
        video.pause();
        video.muted = false;
        video.play().catch(() => {
          // Fallback: some browsers still block — re-mute and retry
          video.muted = true;
          video.play().catch(() => {});
        });
      }
    }
  };

  const handleComplete = () => {
    if (fadeRef.current) clearInterval(fadeRef.current);
    try { audioRef.current?.pause(); } catch {}
    try { videoRef.current?.pause(); } catch {}
    onComplete();
  };

  const handleReplay = () => {
    if (fadeRef.current) clearInterval(fadeRef.current);
    try { videoRef.current?.pause(); } catch {}
    const audio = audioRef.current;
    if (audio) { audio.currentTime = 0; audio.volume = 0.15; audio.play().catch(() => {}); }
    setVideoFading(false); setVideoText(false); setShowText(false); setMuted(false);
    setPhase('slide1');
  };

  return (
    <div className="onboarding-container" ref={containerRef} data-testid="onboarding-container">
      <audio ref={audioRef} src={AUDIO_SRC} preload="auto" data-testid="onboarding-audio" />

      <video ref={videoRef} src={VIDEO_SRC} autoPlay muted playsInline webkit-playsinline="true" preload="auto"
        onEnded={handleVideoEnded} data-testid="onboarding-video"
        className={`ob-media-element ${phase === 'video' && !videoFading ? 'ob-media-visible' : 'ob-media-hidden'} ${videoFading ? 'ob-media-fading' : ''}`}
      />

      {/* Overlay logo doré pour masquer le watermark VEO — CSS only */}
      {phase === 'video' && !videoFading && (
        <div className="ob-veo-overlay" data-testid="veo-overlay">
          <img src="/bobine-face-logo.png" alt="" className="ob-veo-logo" />
        </div>
      )}

      {/* Texte storytelling sur la vidéo */}
      {phase === 'video' && !videoFading && (
        <div className={`ob-video-text ${videoText ? 'visible' : ''}`} data-testid="video-text">
          <h2 className="ob-title">{t('ob_video_text')}</h2>
        </div>
      )}

      {/* SLIDE 1 */}
      <div className={`ob-layer ${phase === 'slide1' ? 'active' : ''}`} data-testid="onboarding-slide-0">
        <div className="ob-photo-frame">
          <img src={SLIDE1_IMG} alt="Souvenir" className={`ob-photo ${phase === 'slide1' ? 'focus-reveal' : ''}`} />
          <div className="ob-photo-glow" />
        </div>
        <div className={`ob-text-overlay ${phase === 'slide1' && showText ? 'visible' : ''}`}>
          <h2 className="ob-title" data-testid="slide-title-0">{t('ob_slide1')}</h2>
        </div>
      </div>

      {/* SLIDE 2 */}
      <div className={`ob-layer ${phase === 'slide2' ? 'active' : ''}`} data-testid="onboarding-slide-1">
        <div className="ob-photo-frame">
          <img src={SLIDE2_IMG} alt="Restauré" className="ob-photo" />
        </div>
        <div className={`ob-text-overlay ${phase === 'slide2' && showText ? 'visible' : ''}`}>
          <h2 className="ob-title" data-testid="slide-title-1">{t('ob_slide2')}</h2>
        </div>
      </div>

      {/* CTA — Logo identique à BobineLogo (B + face + BINE) */}
      <div className={`ob-layer ob-cta-layer ${phase === 'cta' ? 'active' : ''}`} data-testid="onboarding-slide-3">
        <div className="ob-cta-content" data-testid="onboarding-cta-block">
          <div className="ob-cta-logo-block" data-testid="bobine-spinning">
            <span className="ob-cta-brand-letter">B</span>
            {/* Mini-vidéo de la fondatrice intégrée à la place du O — cercle doré, autoplay muet en boucle */}
            <span className="ob-cta-logo-face-wrap" aria-hidden="true">
              <video
                src="/bobine-onboarding-cta.mp4"
                autoPlay
                muted
                loop
                playsInline
                webkit-playsinline="true"
                preload="auto"
                className="ob-cta-logo-face ob-cta-logo-video"
                data-testid="cta-founder-video"
              />
            </span>
            <span className="ob-cta-brand-letter">BINE</span>
          </div>
          <h2 className="ob-cta-message" data-testid="onboarding-cta-title">
            {t('ob_cta_text')}<br />{t('ob_cta_text2')}
          </h2>
          <button className="ob-enter-btn" onClick={handleComplete} data-testid="enter-coffre-button">
            {t('ob_enter')}
          </button>
          <button className="ob-replay-btn" onClick={handleReplay} data-testid="replay-btn">
            {t('ob_replay')}
          </button>
        </div>
      </div>

      {phase !== 'video' && <div className="ob-dark-overlay" />}

      <div className="gold-dust-background">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="dust-particle-onboarding" style={{
            left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`, animationDuration: `${3 + Math.random() * 3}s`
          }} />
        ))}
      </div>

      <button onClick={toggleMute} data-testid="melody-mute-btn" className="ob-mute-btn">
        {muted ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
          </svg>
        )}
      </button>

      <button onClick={handleComplete} data-testid="skip-onboarding-btn" className="ob-skip-btn">{t('ob_skip')}</button>

      {/* Bouton "Suivant" — Lingot d'Or — affiché sur les 3 phases narratives */}
      {(phase === 'slide1' || phase === 'slide2' || phase === 'video') && (
        <button onClick={goNext} data-testid="onboarding-next-btn" className="ob-next-btn">
          {t('ob_next')}
        </button>
      )}

      <div className="ob-indicators" data-testid="slide-indicators">
        {PHASES.map((_, idx) => (
          <div key={idx} className={`ob-dot ${idx <= phaseIndex ? 'active' : ''}`} data-testid={`indicator-${idx}`} />
        ))}
      </div>
    </div>
  );
};

export default OnboardingSlides;
